import { Divider } from '../../../core-components';
import { Icon } from '../../../core-components';
import { Tag } from '../../../core-components';
import { IconButton } from '../../../core-components';
import { Image } from '../../shared/Image';
import { Popover, PopoverContent, PopoverTrigger } from '../../shared/Popover';

import type { I18NInterface } from '../../../utils/i18n';
import type { ComponentPropsWithoutRef } from 'react';

export type ItineraryCardProps = {
  i18n?: I18NInterface<'tenderPort'>;
  variant?: 'port' | 'seaday';
  day: string;
  date: string;
  image?: ComponentPropsWithoutRef<typeof Image>;
  from?: string;
  arrival?: {
    label: string;
    time: string;
  };
  departure?: {
    label: string;
    time: string;
  };
  badge?: string;
  to?: string;
  port?: string;
  isTender?: boolean;
  tenderHeadline?: string;
  tenderSubline?: string;
};

const ItineraryCard = (props: ItineraryCardProps) => {
  const {
    variant = 'port',
    day,
    arrival,
    departure,
    date,
    badge,
    image,
    from,
    to,
    port,
    isTender,
    tenderHeadline,
    tenderSubline,
    i18n,
  } = props;

  const renderTenderTag = () => (
    <Popover>
      <PopoverTrigger
        className="focus:outline-none focus:ring-[3px] focus:shadow-focus-state"
        aria-label="sale-info"
      >
        <IconButton iconName="help-circle" size="xs" variant="ghost" />
      </PopoverTrigger>
      <PopoverContent>
        <div className="max-w-[232px] space-y-2">
          <div className="text-sm font-semibold uppercase text-marine-high-emphasis">
            {tenderHeadline}
          </div>
          <div className="text-xs text-marine-high-emphasis">
            {tenderSubline}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );

  return (
    <div className="flex items-stretch self-stretch rounded-md bg-white px-4 py-3 md:px-2 md:py-2 sm:justify-between">
      <div className="flex items-stretch gap-4 md:px-4 md:py-2">
        <div className="flex min-w-[117px] flex-grow flex-col">
          <span className="headline-sm font-semibold text-marine-high-emphasis">
            {day}
          </span>
          <span className="text-sm text-marine-medium-emphasis">{date}</span>
        </div>
        <Divider
          align="vertical"
          className="place-self-stretch"
          color="secondary-20"
        />
        {variant === 'port' && (
          <div className="flex flex-grow flex-col">
            {isTender && (
              <Tag>
                <div className="flex items-center gap-1 text-xs font-semibold">
                  {i18n?.tenderPort || 'Tender port'}
                  {renderTenderTag()}
                </div>
              </Tag>
            )}
            {port && (
              <span className="label-md font-semibold text-marine-high-emphasis">
                {port}
              </span>
            )}
            {arrival && (
              <div className="flex items-center gap-1 text-marine-medium-emphasis">
                <Icon name="clock" size="xs" />
                <span className="text-sm font-semibold">{arrival.label}</span>
                <span className="text-sm">{arrival.time}</span>
              </div>
            )}
            {departure && (
              <div className="flex items-center gap-1 text-marine-medium-emphasis">
                <Icon name="clock" size="xs" />
                <span className="text-sm font-semibold">{departure.label}</span>
                <span className="text-sm">{departure.time}</span>
              </div>
            )}
          </div>
        )}
        {variant === 'seaday' && (
          <div className="flex flex-grow flex-col">
            <div className="label-md font-semibold text-marine-high-emphasis">
              {badge}
            </div>
            <div className="flex items-center gap-1 text-sm text-marine-high-emphasis">
              {from}
              <Icon name="arrow-right" size="xs" />
              {to}
            </div>
          </div>
        )}
      </div>
      {image && variant === 'port' && (
        <Image
          sizes={['120px']}
          className="hidden aspect-16/9 w-[119px] rounded-sm sm:flex"
          {...image}
        />
      )}
    </div>
  );
};

ItineraryCard.displayName = 'ItineraryCard';

export { ItineraryCard };
